import * as React from 'react'
import {
    Dispatch,
    FormEvent,
    FunctionComponent,
    PropsWithChildren,
    ReactElement,
    SetStateAction,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import * as Yup from 'yup'
import { Schema } from 'yup'
import { Formik, useFormikContext } from 'formik'
import {
    ChoiceGroup,
    IChoiceGroupOption,
    IComboBox,
    IconButton,
    ITextField,
    ITextFieldProps,
    mergeStyleSets,
    Stack,
    StackItem,
} from '@fluentui/react'
import { InvoiceStatus } from '../../../config/finance/invoiceConfig'
import { Billable, BudgetMonitoringDto, mapTimeOverviewDtoToMutation } from '../../../config/registration/timeConfig'
import { IFileDto, RateDto } from '../../../config/fileConfig'
import { useSelector } from 'react-redux'
import { Store } from '../../../reducers'
import FileDropdown from '../../Dropdown/File/FileDropdown'
import DurationTextField from '../DurationTextField'
import MoneyField from '../MoneyField'
import LegacyUserDropdown from '../../Dropdown/LegacyUserDropdown'
import { getDropdownProps } from '../../../helpers/formHelper'
import {
    convertNumber,
    formatTimeInMinutes,
    multiplyTimeNumber,
    parseTime,
    parseTimeInHours,
    parseTimeInMinutes,
} from '../../../helpers/numberHelper'
import Form, { EntityFormProps } from '../Form'
import LegacyLocalDatePicker from '../../../components/LegacyLocalDatePicker'
import { formatIsoDate, parseDate } from '../../../helpers/dateHelper'
import { TimeMutationDto, TimeOverviewDto } from '../../../config/registrationConfig'
import FormikTextField from '../../../components/Formik/TextField/FormikTextField'
import MessageContext from '../../../context/MessageContext'
import { isOfficeInitialized } from '../../../helpers/outlookHelper'
import useUser from '../../../hooks/useUser'
import useAxios from '../../../hooks/useAxios'
import FormContainer from '../../../components/Form/FormContainer'
import useViewport, { Viewport } from '../../../hooks/useViewport'
import { usePrevious } from '@fluentui/react-hooks'
import { NormalizedError } from '../../../reducers/errorReducer'
import { useSettings } from '../../../hooks/useSettings'
import { ModuleType } from '../../../context/GlobalContext'
import useRoles, { rolesWriteAllTimes } from '../../../hooks/useRoles'
import useModules from '../../../hooks/module/useModules'
import { BudgetMonitoringModuleDto, useFileModule } from '../../../hooks/module/useFileModule'
import { MessageBar } from '@fluentui/react-components'
import { useTimeDivisorSetting } from '../../../hooks/file/useTimeDivisorSetting'
import { useTimeCategoriesSetting } from '../../../hooks/file/useTimeCategoriesSetting'
import { IRefObject } from '@fluentui/utilities'
import BaseDropdown from '../../Dropdown/BaseDropdown'
import { useTranslation } from 'react-i18next'
import { BillableLabels } from '../../TagPicker/BillablePicker'
import useCurrency from '../../../hooks/useCurrency'
import { isActiveTier } from '../../../config/finance/financeConfig'
import OverflowMenuPanel from '../../OverflowMenu/OverflowMenuPanel'

type Props = PropsWithChildren<EntityFormProps<TimeOverviewDto, TimeMutationDto>> & { globalError?: NormalizedError }

const getValidationSchema = (
    hasTimeCategoryModule: boolean,
    translate: (key: string, options?: any) => string,
): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        category: hasTimeCategoryModule
            ? Yup.string().label(translate('time.category.label')).required()
            : Yup.string().label(translate('time.category.label')).nullable(),
        description: Yup.string().label(translate('time.description')).required(),
        fileId: Yup.string().uuid().label(translate('time.fileid')).required(),
        duration: Yup.string()
            .required()
            .test('is-valid', translate('time.duration.isvalid'), value => !isNaN(parseTimeInMinutes(value)))
            .test('is-valid-minutes', translate('time.duration.isvalidminutes'), value => parseTime(value).minutes < 60)
            .test(
                'is-less-than-a-minute',
                translate('time.duration.islessminute'),
                value => parseTimeInMinutes(value) >= 1,
            )
            .test(
                'is-more-than-a-day',
                translate('time.duration.ismoreday'),
                value => parseTimeInMinutes(value) <= 24 * 60,
            ),
        rate: Yup.string().label(translate('time.rate')).required(),
        userId: Yup.string().uuid().label(translate('time.userid')).required(),
        date: Yup.string().label(translate('time.date')).required(),
        billable: Yup.string().label(translate('time.billable')).required(),
    })
}

const billableOptions: Omit<IChoiceGroupOption, 'text'>[] = [
    { key: Billable.Billable },
    { key: Billable.Offered },
    { key: Billable.NotBillable },
]

const getRateValue = (values: TimeMutationDto, initialValues: TimeMutationDto, rates: RateDto): string => {
    if (
        values.userId === initialValues.userId &&
        values.fileId === initialValues.fileId &&
        values.billable === initialValues.billable &&
        values.billable === Billable.Billable &&
        values.date === initialValues.date &&
        initialValues.rate
    ) {
        // If nothing of interest has changed from the initial form, inherit the initial value.
        return initialValues.rate
    }
    // Determine the rate from the rates table.
    return (
        (values.billable === Billable.Billable &&
            (rates.rate ||
                (rates.tiers || [])
                    .filter(tier => isActiveTier(tier, parseDate(values.date)))
                    .find(tier => tier.userId === values.userId)?.salesRate)) ||
        '0.00'
    )
}

export enum Direction {
    // Transform from the field value to the DTO value.
    FieldToDto,
    // Transform from the DTO value to the field value.
    DtoToField,
}

export function transformCurrency(value: string, direction: Direction): string {
    switch (direction) {
        case Direction.DtoToField:
            return convertNumber(value, 'en', 'nl-nl', '0.00')
        case Direction.FieldToDto:
            return convertNumber(value, 'nl-nl', 'en', '0.00')
        default:
            return value
    }
}

export const TimeFormContainer: FunctionComponent<{
    useFileContext: boolean
    invoiceStatus?: InvoiceStatus
    currentFile?: IFileDto
    setValidationSchema: Dispatch<SetStateAction<Schema>>
}> = props => {
    const axios = useAxios()
    const settings = useSettings()
    const { initialValues, errors, values, handleChange, setFieldValue } = useFormikContext<TimeMutationDto>()
    const previousValues = usePrevious(values)
    const context = useFormikContext<TimeMutationDto>()
    const { fileIdMap } = useContext(MessageContext)
    const { t: translate } = useTranslation()

    const isInvoiceDraft: boolean = props.invoiceStatus === InvoiceStatus.Draft || false
    const isInvoiceFinal: boolean = [InvoiceStatus.Final, InvoiceStatus.Done].includes(props.invoiceStatus)

    const modules = useModules()
    const [currentFile, setCurrentFile] = useState<IFileDto>(undefined)

    const {
        fileModule: budgetMonitoringModule,
        has: hasBudgetMonitoringModule,
        setCurrentFile: setBudgetMonitoringModuleFile,
    } = useFileModule<BudgetMonitoringModuleDto>(ModuleType.BudgetMonitoring, props.currentFile)

    const {
        hasTimeCategoriesModule,
        timeCategories,
        defaultCategory: defaultTimeCategory,
        setCurrentFile: setTimeCategoriesFile,
    } = useTimeCategoriesSetting()

    const visibleBillableOptions = useMemo<IChoiceGroupOption[]>(
        () =>
            billableOptions.map(option => ({
                ...option,
                text: translate(BillableLabels.get(option.key as Billable).name),
                disabled: isInvoiceFinal || option.disabled,
            })),
        [values.billable],
    )

    //set fixed rate, but replace value once settings have been loaded
    const [rates, setRates] = useState<RateDto>({ rate: '0.00', currency: 'EUR' })
    const { currency } = useCurrency(values.currency)
    const currencySymbol = useMemo<string>(() => currency?.symbol || currency?.alpha || '¤', [currency])
    const [budgetMonitoring, setBudgetMonitoring] = useState<BudgetMonitoringDto>(undefined)

    // These fields have a different display value than what they actually carry.
    const [rateField, setRateField] = useState<string>()
    const [turnoverField, setTurnoverField] = useState<string>()
    const [billableField, setBillableField] = useState<string>(Billable.Billable)

    const { isViewport, getGap } = useViewport()
    const roles = useRoles()

    const styles = mergeStyleSets({
        resetDescription: { backgroundColor: 'transparent' },
    })

    const categoryFocusRef: IRefObject<any> = React.createRef<IComboBox>()
    const durationFocusRef = React.createRef<ITextField>()
    const descriptionFocusRef = React.createRef<ITextField>()

    useEffect(() => {
        setRateField(transformCurrency(initialValues.rate, Direction.DtoToField) as string)
        setBillableField(initialValues.billable)
    }, [initialValues])

    useEffect(() => {
        setRateField(transformCurrency(values.rate, Direction.DtoToField))
        setTurnoverField(multiplyTimeNumber(values.duration, values.rate, '0.00', 'en'))
    }, [values.rate, values.duration])

    useEffect(() => {
        // Always set the rate globally first.
        setRates({
            rate: settings?.fileScaffold?.fileRate?.hourRate || '0.00',
            currency: settings?.fileScaffold?.currency || 'EUR',
        })
        if (values.fileId) {
            // Fetch the rate when the file has changed.
            axios.get<RateDto>(`/file/${values.fileId}/rate`).then(response => {
                setRates(response.data)
            })
            axios.get<IFileDto>(`/file/${values.fileId}`).then(response => {
                setCurrentFile(response.data)
                setBudgetMonitoringModuleFile(response.data)
                setTimeCategoriesFile(response.data)
            })
        }
    }, [values.fileId, values.billable])

    useEffect(() => {
        if (rates) {
            setFieldValue('rate', getRateValue(values, initialValues, rates))
            setFieldValue('currency', rates.currency)
            // Set the persisted value when editing or the response value when creating a time.
            setBillableField(values.billable || rates.billable)
            setFieldValue('billable', values.billable || rates.billable)
        }
    }, [rates, values.userId, values.fileId, values.billable, values.date])

    useEffect(() => {
        if (hasBudgetMonitoringModule) {
            // Fetch the outstanding times when the file has changed.
            axios.get<BudgetMonitoringDto>(`/time/${values.fileId}/monitoring`).then(response => {
                setBudgetMonitoring(response.data)
            })
        }
    }, [hasBudgetMonitoringModule])

    const exceedsBudget = useMemo<boolean>(() => {
        let currentAmount = 0
        if (budgetMonitoringModule && budgetMonitoringModule.billableHours > 0 && budgetMonitoring && values.duration) {
            currentAmount = budgetMonitoring.outstandingHours
            if (budgetMonitoringModule.includeBilledHours) {
                currentAmount += budgetMonitoring.billedHours
            }
            currentAmount += parseTimeInMinutes(values.duration) / 60
            return currentAmount >= budgetMonitoringModule.billableHours
        }

        return false
    }, [hasBudgetMonitoringModule, budgetMonitoring, values.duration])

    useEffect(() => {
        if (hasTimeCategoriesModule) {
            setFieldValue(
                'description',
                values.description ??
                    defaultTimeCategory(values.emailInbound)?.defaultDescription ??
                    initialValues.description,
            )
        }
    }, [timeCategories])

    const durationDeviatesFromTimeDivisor = useMemo<boolean>(() => {
        const time = parseTimeInMinutes(values.duration)
        return time > 0 && time % settings.timeDivisor > 0
    }, [values, settings])

    useEffect(() => {
        props.setValidationSchema(getValidationSchema(hasTimeCategoriesModule, translate))

        //always set optional default text when processing a time suggestion
        if (initialValues.emailInbound !== undefined && hasTimeCategoriesModule) {
            setFieldValue('category', defaultTimeCategory(initialValues.emailInbound)?.key)
        } else {
            setFieldValue(
                'category',
                values.category ??
                    (hasTimeCategoriesModule
                        ? defaultTimeCategory(initialValues.emailInbound)?.key
                        : initialValues.category),
            )
        }
    }, [hasTimeCategoriesModule, timeCategories])

    useEffect(() => {
        // Only calculate the default rate if either of the values has changed; not when the rates have actually loaded.
        if (previousValues?.userId !== values.userId || previousValues?.billable !== values.billable || !values.rate) {
            setFieldValue('rate', getRateValue(values, initialValues, rates))
        }
    }, [rates, values.userId, values.fileId, values.billable, values.date, values.duration])

    // Set fileId when we've selected an email in Outlook and email has been processed.
    useEffect(() => {
        if (isOfficeInitialized() && fileIdMap?.size === 1 && !values.fileId) {
            const [fileId] = fileIdMap.values()
            setFieldValue('fileId', fileId)
        }
    }, [fileIdMap])

    useEffect(() => {
        if (!!categoryFocusRef.current && !isInvoiceFinal && !initialValues.category) {
            categoryFocusRef.current.focus()
        } else if (!isInvoiceFinal && !initialValues.description) {
            descriptionFocusRef.current.focus()
        } else if (!isInvoiceFinal && !!initialValues.description) {
            durationFocusRef.current.focus()
        }
    }, [hasTimeCategoriesModule])

    const timeDivisorSetting = useTimeDivisorSetting()
    const durationSuggestions = useMemo<string[]>(() => {
        if (!timeDivisorSetting.timeDivisor || timeDivisorSetting.timeDivisor < 2) {
            return []
        }

        return [
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 2),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 3),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 4),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 5),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 6),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 7),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 8),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 9),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 10),
        ]
    }, [timeDivisorSetting])

    return (
        <Stack tokens={{ childrenGap: 's1' }}>
            {isInvoiceDraft && <MessageBar intent={'warning'}>{translate('time.edit.form.invoice.draft')}</MessageBar>}
            {isInvoiceFinal && <MessageBar intent={'error'}>{translate('time.edit.form.invoice.final')}</MessageBar>}
            {budgetMonitoringModule && budgetMonitoring && (
                <MessageBar intent={exceedsBudget ? 'error' : 'info'}>
                    {exceedsBudget && translate('time.edit.form.budget.exceeds')}
                    {!exceedsBudget && (
                        <div
                            dangerouslySetInnerHTML={{
                                __html: translate('time.edit.form.budget.notexceeds', {
                                    billedHours: parseTimeInHours(budgetMonitoring.billedHours),
                                    outstandingHours: parseTimeInHours(budgetMonitoring.outstandingHours),
                                    budget: parseTimeInHours(budgetMonitoringModule.billableHours),
                                }),
                            }}
                        />
                    )}
                </MessageBar>
            )}
            {durationDeviatesFromTimeDivisor && (
                <MessageBar intent={'warning'}>
                    {translate('time.edit.form.divisor', { divisor: settings.timeDivisor })}
                </MessageBar>
            )}
            {hasTimeCategoriesModule && (
                <BaseDropdown
                    {...getDropdownProps(
                        values.category,
                        undefined,
                        value => {
                            setFieldValue('category', value)
                            setFieldValue(
                                'description',
                                (timeCategories || []).find(cat => cat.key === value)?.defaultDescription ??
                                    values.description,
                            )
                        },
                        false,
                    )}
                    label={translate('time.category.label')}
                    canClear
                    placeholder={translate('time.category.placeholder')}
                    readOnly={isInvoiceFinal}
                    options={timeCategories.map(category => ({
                        key: category.key,
                        text: category.name,
                    }))}
                    componentRef={categoryFocusRef}
                />
            )}
            <FormikTextField
                context={context}
                label={translate('time.description')}
                fieldName="description"
                textFieldProps={{
                    required: true,
                    multiline: true,
                    rows: 3,
                    // autoFocus: !isInvoiceFinal && !initialValues.description,
                    autoAdjustHeight: true,
                    readOnly: isInvoiceFinal,
                    componentRef: descriptionFocusRef,
                    onRenderLabel: (
                        textFieldProps: ITextFieldProps,
                        defaultRender?: (props?: ITextFieldProps) => JSX.Element | null,
                    ) => (
                        <Stack horizontal verticalAlign={'center'}>
                            {defaultRender(textFieldProps)}
                            <IconButton
                                iconProps={{ iconName: 'Delete' }}
                                tabIndex={-1}
                                onClick={() => {
                                    setFieldValue('description', '')
                                    descriptionFocusRef.current.focus()
                                }}
                                disabled={!values.description || isInvoiceFinal}
                                className={styles.resetDescription}
                            />
                        </Stack>
                    ),
                }}
            />
            <Stack horizontal={isViewport('>=', Viewport.Medium)} tokens={{ childrenGap: getGap() }}>
                <StackItem grow>
                    <DurationTextField
                        label={translate('time.duration.label')}
                        value={values.duration}
                        errorMessage={errors?.duration}
                        onChange={handleChange('duration')}
                        readOnly={isInvoiceFinal}
                        componentRef={durationFocusRef}
                    />
                </StackItem>
                {modules.has(ModuleType.Invoices) && (
                    <>
                        <StackItem grow>
                            <MoneyField
                                label={translate('time.rate')}
                                value={rateField}
                                prefix={currencySymbol}
                                errorMessage={errors?.rate}
                                onChange={(_: FormEvent, newValue?: string) => setRateField(newValue || '0,00')}
                                onFormat={(_: FormEvent, newValue?: string) =>
                                    setFieldValue('rate', transformCurrency(newValue, Direction.FieldToDto))
                                }
                                readOnly={isInvoiceFinal || values.billable !== Billable.Billable}
                                tabIndex={-1}
                            />
                        </StackItem>
                        <StackItem grow>
                            <MoneyField
                                label={translate('time.edit.total')}
                                value={turnoverField}
                                prefix={currencySymbol}
                                readOnly
                                tabIndex={-1}
                            />
                        </StackItem>
                    </>
                )}
            </Stack>
            <Stack horizontal tokens={{ childrenGap: getGap() }}>
                {durationSuggestions.length > 0 && (
                    <OverflowMenuPanel
                        data={durationSuggestions}
                        minimumVisible={3}
                        overflowAxis={'horizontal'}
                        onItemClick={value => setFieldValue('duration', value)}
                    />
                )}
            </Stack>
            <FileDropdown
                readOnly={isInvoiceFinal || isInvoiceDraft}
                initialValues={[props.currentFile?.id || currentFile?.id]}
                {...getDropdownProps(values.fileId, errors?.fileId, value => setFieldValue('fileId', value))}
                tabIndex={props.useFileContext ? -1 : undefined}
            />
            <LegacyUserDropdown
                label={translate('time.userid')}
                {...getDropdownProps(values.userId, errors?.userId, value => setFieldValue('userId', value))}
                readOnly={isInvoiceFinal || !roles.any(rolesWriteAllTimes)}
                tabIndex={-1}
            />
            <LegacyLocalDatePicker
                value={values.date}
                onSelectDate={date => setFieldValue('date', date)}
                datePickerProps={{
                    label: translate('time.date'),
                    ...(isInvoiceFinal && {
                        minDate: parseDate(values.date).toBSON(),
                        maxDate: parseDate(values.date).toBSON(),
                    }),
                }}
            />
            {modules.has(ModuleType.Invoices) && (
                <ChoiceGroup
                    label={translate('time.billable')}
                    selectedKey={billableField}
                    readOnly={isInvoiceFinal}
                    onChange={(_: FormEvent, option?: IChoiceGroupOption) => {
                        setBillableField(option?.key)
                        setFieldValue('billable', option?.key)
                    }}
                    options={visibleBillableOptions}
                    required
                />
            )}
        </Stack>
    )
}

export default function TimeForm(props: Props): ReactElement {
    const currentUser = useUser()
    const currentFile = useSelector<Store, IFileDto>(state => state.files.current.data)
    const [validationSchema, setValidationSchema] = useState<Schema>()
    const timeDivisorSetting = useTimeDivisorSetting()

    return (
        <Formik<TimeMutationDto>
            initialValues={
                props.entity?.data
                    ? mapTimeOverviewDtoToMutation(props.entity.data)
                    : {
                          description: '',
                          fileId: props.useFileContext ? currentFile?.id : undefined,
                          userId: currentUser?.id,
                          billable: undefined,
                          date: formatIsoDate(new Date()),
                          duration: timeDivisorSetting.initialDuration,
                          currency: undefined,
                          rate: undefined,
                          category: undefined,
                          ...props.initialValues,
                      }
            }
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={validationSchema}
            onSubmit={props.onSubmit}
        >
            <Form globalErrors={props.globalError ? [props.globalError] : undefined}>
                <FormContainer>
                    <TimeFormContainer
                        useFileContext={props.useFileContext}
                        invoiceStatus={props.entity?.data?.invoice?.status}
                        currentFile={currentFile ? currentFile : undefined}
                        setValidationSchema={setValidationSchema}
                    />
                    {props.children}
                </FormContainer>
            </Form>
        </Formik>
    )
}
